/*********************************************************************************
 * Copyright (C) 2025 VECROS Technologies Pvt. Ltd.
 * <legal@vecros.com>
 * This file is part of VECROS CLOUD SOFTWARE SERVICES maintained and developed by VECROS TECHNOLOGIES PRIVATE LIMITED.
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 **********************************************************************************/

/**
 * Where the accounts app lives. Drive has no login form of its own: sign-in is
 * delegated to accounts, which owns the identity provider and, on success, calls
 * vecros-api `/auth/session` to mint the VCS_SESSION cookie this app runs on.
 *
 * Mirrors wms/src/utils/authEnvironment.js so the two apps resolve identically.
 */

const LOCAL_AUTH_BASE_URL = "http://localhost:3000";
const DEV_AUTH_BASE_URL = "https://dev-accounts.vecros.com";
const PROD_AUTH_BASE_URL = "https://accounts.vecros.com";

const stripTrailingSlash = (value) => value.replace(/\/+$/, "");

export const isLocalhostHostname = (hostname) =>
  hostname === "localhost" || hostname === "127.0.0.1" || hostname === "[::1]";

const isDevHostname = (hostname) =>
  hostname.startsWith("dev-") || hostname.startsWith("dev.");

/**
 * Explicit env override wins; otherwise resolve by hostname so one build works
 * across local, dev and prod without rebuilding.
 */
export const getAuthBaseUrl = () => {
  const configured =
    process.env.REACT_APP_ACCOUNTS_BASE_URL || process.env.REACT_APP_AUTH_BASE_URL;
  if (configured) return stripTrailingSlash(configured);

  const { hostname } = window.location;
  if (isLocalhostHostname(hostname)) return LOCAL_AUTH_BASE_URL;
  if (isDevHostname(hostname)) return DEV_AUTH_BASE_URL;
  return PROD_AUTH_BASE_URL;
};

/**
 * Send the user to the accounts sign-in screen, asking to be returned here.
 *
 * `redirect` is the param accounts reads (`getRedirectDestinationFromSearch`
 * accepts redirect_to / redirectUrl / redirect). Accounts validates it against
 * its own origin allowlist and silently falls back to its root if the origin
 * isn't allowed — so drive's origin must be listed there. See notes in README.
 *
 * `returnTo` defaults to the current path so a deep link survives the round trip.
 */
export const redirectToAccountsLogin = (returnTo) => {
  const destination = new URL(
    returnTo || `${window.location.pathname}${window.location.search}`,
    window.location.origin
  ).href;
  const target = `${getAuthBaseUrl()}/auth/login?redirect=${encodeURIComponent(destination)}`;
  // Full page navigation, not react-router: accounts is a separate origin.
  window.location.assign(target);
};

/** Accounts owns sign-out too, so land the user on its login screen. */
export const redirectToAccountsAfterLogout = () => {
  window.location.assign(`${getAuthBaseUrl()}/auth/login`);
};

/**
 * The non-httpOnly token cookie accounts sets alongside the VCS_SESSION cookie.
 * Drive's own API calls use the session cookie, but the assistant endpoint is
 * mounted behind bearer auth, so it needs this. Returns null when absent.
 */
export const getAuthToken = () => {
  const cookies = document.cookie ? document.cookie.split("; ") : [];
  for (const cookie of cookies) {
    const separator = cookie.indexOf("=");
    if (separator === -1) continue;
    if (cookie.slice(0, separator) === "VCS_TOKEN") {
      return decodeURIComponent(cookie.slice(separator + 1));
    }
  }
  return null;
};
